import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  ShoppingBag,
  FileText,
  Truck,
  Package,
  Box,
  Target,
  CreditCard,
  UserCog,
  BarChart3,
  LogOut,
  Menu,
  X,
  QrCode,
  Tags,
  Wallet,
  ShieldCheck,
  ScrollText,
  KeyRound,
  Globe,
  Settings as SettingsIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout({ children, user, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [pwLoading, setPwLoading] = useState(false);

  // Menu access is privilege-based. Admin has every privilege; custom roles
  // carry whatever privileges were attached to them. `priv: null` = all staff.
  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, priv: null },
    { path: '/customers', label: 'Customers', icon: Users, priv: 'customers' },
    { path: '/orders', label: 'Orders', icon: ShoppingCart, priv: 'orders' },
    { path: '/customer-orders', label: 'Customer Orders', icon: ShoppingBag, priv: 'customer_orders' },
    { path: '/products-management', label: 'Products Catalog', icon: Package, priv: 'products' },
    { path: '/website', label: 'Website', icon: Globe, priv: 'website' },
    { path: '/qr-codes', label: 'QR Codes & Tracking', icon: QrCode, priv: 'qr_codes' },
    { path: '/invoices', label: 'Invoices', icon: FileText, priv: ['invoices', 'proforma_invoices'] },
    { path: '/pricing', label: 'Pricing', icon: Tags, priv: 'pricing' },
    { path: '/finance', label: 'Finance', icon: Wallet, priv: 'expenses' },
    { path: '/delivery-notes', label: 'Delivery Notes', icon: Truck, priv: 'delivery_notes' },
    { path: '/raw-materials', label: 'Raw Materials', icon: Package, priv: 'raw_materials' },
    { path: '/chalk-cartons', label: 'Chalk Cartons', icon: Box, priv: 'chalk_cartons' },
    { path: '/quality', label: 'Quality (TBS)', icon: ShieldCheck, priv: 'quality' },
    { path: '/sales-targets', label: 'Sales Targets', icon: Target, priv: 'sales_targets' },
    { path: '/bank-transactions', label: 'Bank Details', icon: CreditCard, priv: 'bank_transactions' },
    { path: '/reports', label: 'Reports', icon: BarChart3, priv: 'reports' },
    { path: '/staff', label: 'Staff Management', icon: UserCog, priv: 'staff' },
    { path: '/audit-logs', label: 'Audit Logs', icon: ScrollText, priv: 'audit_logs' },
    { path: '/settings', label: 'Settings', icon: SettingsIcon, priv: 'settings' },
  ];

  // Older sessions may not carry privileges — fall back to the legacy role map
  const legacyPrivs = {
    sales: ['customers', 'orders', 'customer_orders', 'payments', 'invoices', 'delivery_notes', 'sales_targets', 'reports', 'quality'],
    finance: ['customer_orders', 'payments', 'invoices', 'bank_transactions', 'raw_materials', 'reports'],
    financials: ['customer_orders', 'payments', 'invoices', 'bank_transactions', 'raw_materials', 'reports'],
    inventory: ['qr_codes', 'delivery_notes', 'raw_materials', 'chalk_cartons', 'quality'],
    'inventory manager': ['qr_codes', 'delivery_notes', 'raw_materials', 'chalk_cartons', 'quality'],
  };
  const role = user?.role?.toLowerCase();
  const privileges = user?.privileges?.length
    ? user.privileges
    : role === 'admin' ? ['*'] : (legacyPrivs[role] || []);

  const hasPriv = (priv) => {
    if (!priv) return true;
    if (privileges.includes('*')) return true;
    const wanted = Array.isArray(priv) ? priv : [priv];
    return wanted.some((p) => privileges.includes(p));
  };
  const filteredMenuItems = menuItems.filter(item => hasPriv(item.priv));

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, pwForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setPwOpen(false);
      setPwForm({ current_password: '', new_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-white">
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-3 mb-2">
              <img src="/himan-logo.png" alt="Himan Logo" className="h-10 w-10 object-contain" />
              <h1 className="text-lg font-bold tracking-tight text-[#010101]">Himan Investment & Company</h1>
            </div>
            <p className="text-sm text-gray-600 mt-2">{user?.name}</p>
            <p className="text-xs text-[#8FC043] capitalize font-semibold">{user?.role}</p>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto bg-[#EA5829]">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    isActive
                      ? 'bg-white text-[#EA5829] font-semibold'
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-white/20 bg-[#EA5829] space-y-1">
            <Button
              data-testid="change-password-button"
              onClick={() => setPwOpen(true)}
              variant="ghost"
              className="w-full justify-start text-white/90 hover:bg-white/10 hover:text-white"
            >
              <KeyRound size={20} className="mr-3" />
              Change Password
            </Button>
            <Button
              data-testid="logout-button"
              onClick={onLogout}
              variant="ghost"
              className="w-full justify-start text-white/90 hover:bg-white/10 hover:text-white"
            >
              <LogOut size={20} className="mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between">
          <Button
            data-testid="mobile-menu-button"
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
          <div className="hidden lg:block text-sm text-slate-500">
            Welcome back, <span className="font-semibold text-slate-700">{user?.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input id="current-password" data-testid="current-password-input" type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password-dialog">New Password</Label>
              <Input id="new-password-dialog" data-testid="new-password-dialog-input" type="password"
                placeholder="At least 8 characters" minLength={8}
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} required />
            </div>
            <Button type="submit" className="w-full" disabled={pwLoading} data-testid="change-password-submit">
              {pwLoading ? 'Saving...' : 'Change Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
